export type XiaohongshuParkingMode = "without-xhs" | "with-xhs";
export type XiaohongshuParkingTaskId = "shopping" | "hospital";

export interface XiaohongshuParkingOption {
  id: string;
  name: string;
  badge: string;
  price: string;
  detail: string;
  walk: string;
  evidence?: string;
  recommended?: boolean;
}

export interface XiaohongshuParkingTask {
  id: XiaohongshuParkingTaskId;
  tabLabel: string;
  title: string;
  query: string;
  withoutXhs: {
    headline: string;
    summary: string;
    options: XiaohongshuParkingOption[];
    limitation: string;
  };
  withXhs: {
    headline: string;
    summary: string;
    options: XiaohongshuParkingOption[];
    insight: string;
    caveat: string;
    evidenceSummary: string;
  };
}

export const XIAOHONGSHU_PARKING_TASKS: Record<
  XiaohongshuParkingTaskId,
  XiaohongshuParkingTask
> = {
  shopping: {
    id: "shopping",
    tabLabel: "逛街停车",
    title: "周末去前滩太古里",
    query: "想找一个免费停车的停车场",
    withoutXhs: {
      headline: "优先推荐目的地停车场",
      summary: "地图可以找到停车场，但只能提供位置、距离和公开收费信息。",
      options: [
        {
          id: "taikoo-li",
          name: "前滩太古里地下停车场",
          badge: "目的地",
          price: "12元/小时",
          detail: "场内消费停车权益需现场确认",
          walk: "步行约50m",
          recommended: true
        },
        {
          id: "l-plus-basic",
          name: "前滩 L+ Plaza",
          badge: "附近",
          price: "收费规则待确认",
          detail: "地图仅显示停车场入口",
          walk: "步行约500m · 约7分钟"
        }
      ],
      limitation: "缺少商场活动和车主实测经验，无法真正回答“哪里能免费停”。"
    },
    withXhs: {
      headline: "找到3个近期免费方案",
      summary: "结合地图、2026年7月实测、官方商户活动与点点对45篇笔记的总结，按便利性和可信度排序。",
      options: [
        {
          id: "l-plus",
          name: "前滩 L+ Plaza",
          badge: "首选",
          price: "近期笔记称周末免费4小时",
          detail: "需会员页领券；已有当日无法领取的反馈",
          walk: "步行约500m · 约7分钟",
          evidence: "点点45篇总结 + 实测 · 2026-07-27",
          recommended: true
        },
        {
          id: "park-lane",
          name: "前滩公园巷",
          badge: "备选",
          price: "近期活动会员免费3小时",
          detail: "官方账号仅确认活动期；日常规则仍需复核",
          walk: "步行约600m",
          evidence: "官方商户 + 点点 · 2026-07"
        },
        {
          id: "qiantan-yuan",
          name: "前滩源停车场",
          badge: "长停备选",
          price: "近期笔记称消费可免4小时",
          detail: "2026年7月新规则，需消费并积分",
          walk: "车程约6分钟",
          evidence: "小红书实测 · 2026-07-04"
        }
      ],
      insight: "小红书补齐了“活动时段、领券入口和失效反馈”，地图负责距离、路线与步行时间。",
      caveat: "停车优惠会动态调整；出发前应再次读取商户规则，进场前确认券是否可领。",
      evidenceSummary: "点点45篇总结 + 近期实测 + 官方商户"
    }
  },
  hospital: {
    id: "hospital",
    tabLabel: "送医停车",
    title: "周末上午9点去东方医院南院",
    query: "送家人看病，怎么停车更省心",
    withoutXhs: {
      headline: "直接导航到院内停车场",
      summary: "地图会优先规划到目的地停车场，只有接近或到达后才处理满位。",
      options: [
        {
          id: "hospital-main",
          name: "东方医院南院停车场",
          badge: "目的地",
          price: "院内停车",
          detail: "到达后查看实时空位",
          walk: "步行约80m",
          recommended: true
        },
        {
          id: "nearby-search",
          name: "附近停车场",
          badge: "到达后搜索",
          price: "按地图距离排序",
          detail: "满位后再重新规划",
          walk: "距离待计算"
        }
      ],
      limitation: "没有时段经验，家人可能跟着车辆在医院周边反复绕行。"
    },
    withXhs: {
      headline: "院内高风险：先落客，再顺路找车位",
      summary: "点点综合17篇笔记判断周末9点非常容易满；该判断不是实时余位，仍需到达前复核。",
      options: [
        {
          id: "hospital-dropoff",
          name: "东方医院南院门诊入口",
          badge: "系统策略",
          price: "短停即走",
          detail: "先让家人在门口下车，不跟车绕行",
          walk: "到院即达",
          evidence: "点点17篇总结 · 2026-07-27",
          recommended: true
        },
        {
          id: "yuntai-roadside",
          name: "恒大翰城东北门（云台路）",
          badge: "周末免费备选",
          price: "周末免费 · 开放需确认",
          detail: "点点与近期实测均指向云台路一带",
          walk: "步行约6–10分钟",
          evidence: "点点17篇 + 实测 · 2026-07"
        },
        {
          id: "yuntai-shangbo",
          name: "云台路×尚博路社会停车场",
          badge: "稳定备选",
          price: "约4–6元/小时 · 价格待复核",
          detail: "点点与2025年实测价格冲突，均指向该路口",
          walk: "步行约5–8分钟",
          evidence: "点点17篇 + 历史实测"
        }
      ],
      insight: "小红书提供周末免费点位、车库体验和价格线索；地图负责把落客点与备选停车场串成顺路路线。",
      caveat: "点点结论来自笔记汇总，不是实时余位；正式产品仍需叠加上海停车预约、开放状态与到达时段。",
      evidenceSummary: "点点17篇总结 + 3条位置实测"
    }
  }
};
